import React from 'react';

import Close from '@rover/icons/svg/close.svg';
import { Box, Button, Flex, Heading, Paragraph } from '@rover/kibble/core';

import type { DialogButtonProps, ModalCommonProps } from '../Modal.common';

import DialogButtons, { hasDialogButtons } from './DialogButtons';
import TitleFocusTarget from './TitleFocusTarget';

export type Props = DialogButtonProps &
  Omit<
    ModalCommonProps,
    | 'isOpen'
    | 'isDismissible'
    | 'shouldCloseOnOverlayClick'
    | 'headerText'
    | 'headerSize'
    | 'headerAlign'
    | 'headerProps'
  > & {
    headerId?: string;
    contentWrapperHeight?: string;
    scrollsBody?: boolean;
    /** Native only: the title's focusable view, which screen readers start on. */
    titleRef?: React.RefObject<unknown>;
  };

export default function ModalContent({
  children,
  showCloseIcon,
  closeIconSize = 'default',
  title,
  headerId,
  titleSize = '300',
  titleAlign = 'left',
  titleProps = {},
  titleRef,
  subtitle,
  customFooter,
  contentWrapperHeight,
  scrollsBody = false,
  onRequestClose,
  ...buttonProps
}: Props): JSX.Element {
  return (
    <Flex
      flexDirection="column"
      width="100%"
      height={contentWrapperHeight}
      {...(scrollsBody && { minHeight: '0px' })}
    >
      {(title || showCloseIcon) && (
        <Flex
          flexDirection="row"
          justifyContent={title ? 'space-between' : 'flex-end'}
          alignItems="center"
          mb={subtitle ? '2x' : '4x'}
          {...titleProps}
        >
          {title && (
            <TitleFocusTarget titleRef={titleRef} fill={titleAlign === 'center'}>
              <Heading
                id={headerId}
                size={titleSize}
                m="0x"
                {...(titleAlign === 'center' && { sx: { flex: 1, textAlign: 'center' } })}
                {...(titleRef && { rxnProps: { accessibilityRole: 'none' } })}
              >
                {title}
              </Heading>
            </TitleFocusTarget>
          )}
          {showCloseIcon && (
            <Box ml="4x">
              <Button
                icon={Close}
                variant="flat"
                onClick={onRequestClose}
                size={closeIconSize}
                data-qa-id="modal-dismiss-button"
                data-testid="modal-dismiss-button"
                aria-label="Dismiss modal"
                sx={{ color: 'text.primary', '&:hover, &:focus': { color: 'text.primary' } }}
                circular
              />
            </Box>
          )}
        </Flex>
      )}
      {subtitle && (
        <Paragraph size="200" mt="0x" mb="4x">
          {subtitle}
        </Paragraph>
      )}
      <Box
        flex="1"
        flexBasis="auto"
        pt="0x"
        {...(scrollsBody && { minHeight: '0px', sx: { overflowY: 'auto' } })}
      >
        {children}
      </Box>
      <DialogButtons {...buttonProps} />
      {customFooter && <Box mt={hasDialogButtons(buttonProps) ? '4x' : '8x'}>{customFooter}</Box>}
    </Flex>
  );
}
